import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, XCircle } from 'lucide-react';

type ToastTone = 'success' | 'error' | 'info';

type ToastPayload = {
  message: string;
  tone?: ToastTone;
  durationMs?: number;
};

type ToastState = {
  id: number;
  message: string;
  tone: ToastTone;
};

type ToastContextValue = {
  showToast: (payload: ToastPayload) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
};

function toneClasses(tone: ToastTone) {
  if (tone === 'success') return 'border-skoleom-lime/50 text-skoleom-lime';
  if (tone === 'error') return 'border-[#ff4d57]/50 text-[#ff4d57]';
  return 'border-white/25 text-white';
}

function ToneIcon({ tone }: { tone: ToastTone }) {
  if (tone === 'success') return <CheckCircle2 size={16} />;
  if (tone === 'error') return <XCircle size={16} />;
  return <Info size={16} />;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback(({ message, tone = 'info', durationMs = 2400 }: ToastPayload) => {
    const id = Date.now();
    setToast({ id, message, tone });

    window.setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, durationMs);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <div className="pointer-events-none fixed bottom-6 right-4 z-[70] w-[min(92vw,26rem)]">
          <div
            role="status"
            aria-live="polite"
            className={`rounded-xl border bg-black/95 px-4 py-3 text-sm font-medium shadow-[0_10px_40px_rgba(0,0,0,0.45)] ${toneClasses(toast.tone)}`}
          >
            <div className="flex items-start gap-2">
              <span className="mt-0.5">
                <ToneIcon tone={toast.tone} />
              </span>
              <p className="leading-relaxed text-white">{toast.message}</p>
            </div>
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}
